import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, BookOpen, QrCode, Users, Send,
  Check, ArrowRight, Mail, Phone, MapPin, Loader2,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { BrandMark } from "../components/Layout";
import { useLang, LangToggle } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import heroPartsbook from "../assets/hero-partsbook.jpeg";
import heroReport from "../assets/hero-report.jpeg";
import heroNewReport from "../assets/hero-newreport.jpeg";

const SLIDES = [
  { src: heroNewReport, label: { en: "Log a full service report on site", nl: "Maak een volledig rapport op locatie" } },
  { src: heroPartsbook, label: { en: "Spare parts book — tap a part on the drawing", nl: "Onderdelenboek — tik een onderdeel op de tekening" } },
  { src: heroReport, label: { en: "Signed reports, sent straight to the office", nl: "Ondertekende rapporten, direct naar kantoor" } },
];

function HeroSlideshow() {
  const { lang } = useLang();
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % SLIDES.length), 3800);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative w-full h-[360px] md:h-[500px] rounded-2xl border border-zinc-200 shadow-xl overflow-hidden flex items-center justify-center p-4" style={{ background: "radial-gradient(circle at 50% 25%, #F1F5F9 0%, #E2E8F0 80%)" }} data-testid="hero-slideshow">
      {SLIDES.map((s, idx) => (
        <img key={idx} src={s.src} alt={lang === "nl" ? s.label.nl : s.label.en}
          className={`absolute max-h-[94%] w-auto object-contain rounded-xl shadow-2xl ring-1 ring-black/5 transition-opacity duration-700 ${idx === i ? "opacity-100" : "opacity-0 pointer-events-none"}`} />
      ))}
      <div className="absolute bottom-3 left-0 right-0 flex flex-col items-center gap-2 px-4">
        <span className="text-xs font-semibold text-white text-center bg-black/45 backdrop-blur px-3 py-1 rounded-full" data-testid="hero-slide-label">
          {lang === "nl" ? SLIDES[i].label.nl : SLIDES[i].label.en}
        </span>
        <div className="flex gap-1.5">
          {SLIDES.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} data-testid={`hero-dot-${idx}`} aria-label={`Slide ${idx + 1}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-[#F26722]" : "w-1.5 bg-zinc-400/60 hover:bg-zinc-500"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

const STORY = "https://images.pexels.com/photos/32845694/pexels-photo-32845694.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  {
    icon: FileText, key: "reports",
    title: { en: "On-site reports", nl: "Rapporten op locatie" },
    desc: {
      en: "Log completed jobs, machines and parts from the field and send them to the office in seconds.",
      nl: "Leg afgeronde klussen, machines en onderdelen vast in het veld en stuur ze in enkele seconden naar kantoor.",
    },
    long: {
      en: "Create a complete service report without ever leaving the machine. Add the customer, call ID and date, write up the work you carried out, attach the machines you serviced and the exact parts you used, then capture the customer's signature right on your phone or tablet. Running over? Save it as a draft and finish later. When you're done, send it in one tap — the office receives the finished, signed PDF in seconds. No more rewriting scribbled notes back at the depot.",
      nl: "Maak een compleet servicerapport zonder de machine te verlaten. Voeg de klant, het call-ID en de datum toe, beschrijf het uitgevoerde werk, koppel de machines die je hebt onderhouden en de exacte onderdelen die je hebt gebruikt, en laat de klant direct op je telefoon of tablet tekenen. Loopt de klus uit? Sla het op als concept en maak het later af. Als je klaar bent, verstuur je het met één tik — kantoor ontvangt binnen enkele seconden de afgeronde, ondertekende PDF. Nooit meer losse notities overtypen op kantoor.",
    },
  },
  {
    icon: BookOpen, key: "parts-book",
    title: { en: "Spare parts book", nl: "Onderdelenboek" },
    desc: {
      en: "Keep every drawing and parts list in one searchable place your whole team can reach.",
      nl: "Bewaar elke tekening en onderdelenlijst op één doorzoekbare plek die je hele team kan bereiken.",
    },
    long: {
      en: "Every exploded-view drawing, parts list and catalogue number for your machines, organised per machine type and searchable in a single tap. Technicians zoom into a drawing, tap a hotspot on the exact part they need, instantly see its number and stock level, and add it straight to the report they're writing. The whole team works from one always-current book, so nobody ever orders the wrong part again.",
      nl: "Elke onderdelentekening, onderdelenlijst en catalogusnummer voor je machines, geordend per machinetype en met één tik doorzoekbaar. Monteurs zoomen in op een tekening, tikken op het exacte onderdeel dat ze nodig hebben, zien direct het nummer en de voorraad, en voegen het meteen toe aan het rapport waar ze mee bezig zijn. Het hele team werkt vanuit één altijd actueel boek, zodat niemand ooit nog het verkeerde onderdeel bestelt.",
    },
  },
  {
    icon: QrCode, key: "qr-scanning",
    title: { en: "QR part scanning", nl: "QR-onderdeel scannen" },
    desc: {
      en: "Scan a part's QR code to pull up its details, catalog number and stock instantly.",
      nl: "Scan de QR-code van een onderdeel om direct de details, het catalogusnummer en de voorraad op te halen.",
    },
    long: {
      en: "Stick a QR code on a part or a shelf and scan it with the app to instantly pull up that part's full details — catalogue number, description, the machine it belongs to and its current stock. It's the fastest way to identify a part in the field or log one back to the office, with no typing and no mistakes.",
      nl: "Plak een QR-code op een onderdeel of een schap en scan deze met de app om direct alle gegevens van dat onderdeel op te halen — catalogusnummer, omschrijving, de machine waar het bij hoort en de actuele voorraad. Het is de snelste manier om een onderdeel in het veld te identificeren of door te geven aan kantoor, zonder typwerk en zonder fouten.",
    },
  },
  {
    icon: Users, key: "team",
    title: { en: "Team & recipients", nl: "Team & ontvangers" },
    desc: {
      en: "Manage technicians, offices and report recipients per company from one console.",
      nl: "Beheer monteurs, kantoren en rapportontvangers per bedrijf vanuit één console.",
    },
    long: {
      en: "Manage your whole organisation from one console. Add technicians and office staff, decide who is allowed to manage parts and machines, and maintain the list of office recipients that finished reports are sent to. Each person gets exactly the right access for their role, and every report automatically reaches the correct inbox — per company and per customer.",
      nl: "Beheer je hele organisatie vanuit één console. Voeg monteurs en kantoormedewerkers toe, bepaal wie onderdelen en machines mag beheren, en onderhoud de lijst met kantoorontvangers naar wie afgeronde rapporten worden gestuurd. Iedereen krijgt precies de juiste toegang voor zijn rol, en elk rapport komt automatisch in de juiste inbox terecht — per bedrijf en per klant.",
    },
  },
  {
    icon: Send, key: "delivery",
    title: { en: "Instant delivery", nl: "Direct versturen" },
    desc: {
      en: "Signed reports land in the right inbox automatically the moment a job is closed.",
      nl: "Ondertekende rapporten komen automatisch in de juiste inbox zodra een klus is afgesloten.",
    },
    long: {
      en: "The moment a technician closes a job, the signed report is generated as a professional PDF and emailed straight to the chosen office recipient — along with an Excel summary of the parts that were used. No syncing, no uploading, no waiting until they're back at base. The right people have the paperwork before the van has even left the site.",
      nl: "Zodra een monteur een klus afsluit, wordt het ondertekende rapport als een professionele PDF gegenereerd en direct per e-mail naar de gekozen kantoorontvanger gestuurd — samen met een Excel-overzicht van de gebruikte onderdelen. Geen synchroniseren, geen uploaden, geen wachten tot ze terug zijn op de zaak. De juiste mensen hebben de administratie al voordat de bus de locatie heeft verlaten.",
    },
  },
  {
    icon: ShieldCheck, key: "2fa",
    title: { en: "2FA secured", nl: "Beveiligd met 2FA" },
    desc: {
      en: "Every account is protected with mandatory authenticator-based two-factor login.",
      nl: "Elk account is beschermd met verplichte tweefactorlogin via een authenticator-app.",
    },
    long: {
      en: "Security isn't optional. Every account is protected with mandatory two-factor authentication using an authenticator app such as Google Authenticator or Authy. Combined with strong password rules and single-use backup recovery codes, it keeps your customer data, reports and parts information safe — even if a password is ever lost or stolen.",
      nl: "Beveiliging is niet optioneel. Elk account wordt beschermd met verplichte tweefactorauthenticatie via een authenticator-app zoals Google Authenticator of Authy. In combinatie met strenge wachtwoordregels en eenmalig te gebruiken back-up herstelcodes houdt dit je klantgegevens, rapporten en onderdeleninformatie veilig — zelfs als een wachtwoord ooit verloren raakt of gestolen wordt.",
    },
  },
];

const STEPS = [
  { icon: FileText, title: { en: "Log the job", nl: "Leg de klus vast" }, body: { en: "Add the customer, call ID, date and the machines you serviced — all from your phone.", nl: "Voeg de klant, het call-ID, de datum en de machines die je hebt onderhouden toe — alles vanaf je telefoon." } },
  { icon: QrCode, title: { en: "Add parts & signature", nl: "Onderdelen & handtekening" }, body: { en: "Scan or pick parts from the spare parts book and let the customer sign on your screen.", nl: "Scan of kies onderdelen uit het onderdelenboek en laat de klant op je scherm tekenen." } },
  { icon: Send, title: { en: "Straight to the office", nl: "Direct naar kantoor" }, body: { en: "Send the signed PDF to the right office recipient the moment the job is done.", nl: "Verstuur de ondertekende PDF naar de juiste kantoorontvanger zodra de klus klaar is." } },
];

const SHOWCASE = [
  {
    theme: { en: "On site", nl: "Op locatie" },
    img: heroNewReport,
    title: { en: "Build the full report at the machine", nl: "Maak het volledige rapport bij de machine" },
    body: { en: "Customer, call ID, machines, parts and the work performed — then capture the customer's signature right on your phone. Save as a draft or send it instantly.", nl: "Klant, call-ID, machines, onderdelen en het uitgevoerde werk — en laat de klant direct op je telefoon tekenen. Sla op als concept of verstuur meteen." },
    bullets: { en: ["Signature captured on site", "Save as draft, finish later", "No paperwork back at the depot"], nl: ["Handtekening op locatie", "Opslaan als concept, later afmaken", "Geen papierwerk op kantoor"] },
  },
  {
    theme: { en: "Spare parts", nl: "Onderdelen" },
    img: heroPartsbook,
    title: { en: "Find any part in seconds", nl: "Vind elk onderdeel in seconden" },
    body: { en: "Exploded-view drawings with tap-to-select hotspots, catalogue numbers and stock levels — add the exact part straight into the report you're writing.", nl: "Explosietekeningen met aantikbare hotspots, catalogusnummers en voorraad — voeg het juiste onderdeel direct toe aan het rapport waar je mee bezig bent." },
    bullets: { en: ["Tap a part on the drawing", "Catalogue number & stock", "Organised per machine type"], nl: ["Tik een onderdeel op de tekening", "Catalogusnummer & voorraad", "Geordend per machinetype"] },
  },
  {
    theme: { en: "To the office", nl: "Naar kantoor" },
    img: heroReport,
    title: { en: "Signed reports, sent instantly", nl: "Ondertekende rapporten, direct verstuurd" },
    body: { en: "The moment a job closes, a professional signed PDF — plus an Excel summary of the parts used — is e-mailed to the right office recipient automatically.", nl: "Zodra een klus klaar is, wordt automatisch een professionele ondertekende PDF — plus een Excel-overzicht van de gebruikte onderdelen — naar de juiste kantoorontvanger gemaild." },
    bullets: { en: ["Professional PDF + parts Excel", "Automatic e-mail delivery", "Re-send to any office anytime"], nl: ["Professionele PDF + onderdelen-Excel", "Automatische e-maillevering", "Altijd opnieuw te versturen"] },
  },
];

const PLAN = {
  setupFee: "€189",
  period: { en: "/ user / month", nl: "/ gebruiker / maand" },
  trial: { en: "7-day free trial", nl: "7 dagen gratis proberen" },
};
const PLANS = [
  { id: "standard", name: "Standard", price: "€25",
    tagline: { en: "Everything your team needs to report in the field.", nl: "Alles wat je team nodig heeft om in het veld te rapporteren." },
    features: { en: ["Service reports", "Parts books", "Scanning", "PDF & Excel export", "Team management"], nl: ["Servicerapporten", "Onderdelenboeken", "Scannen", "Pdf- & Excel-export", "Teambeheer"] } },
  { id: "pro", name: "Pro", price: "€30", featured: true,
    tagline: { en: "Plan your work and see how your company is doing.", nl: "Plan je werk en zie hoe je bedrijf ervoor staat." },
    features: { en: ["Everything in Standard", "Planning", "Dashboard", "Machine checklists"], nl: ["Alles uit Standard", "Planning", "Dashboard", "Machinechecklists"] } },
];
export function PublicNav() {
  const { lang } = useLang();
  const tt = (en, nl) => (lang === "nl" ? nl : en);
  return (
    <header className="surface-dark sticky top-0 z-50 border-b border-zinc-800/60">
      <div className="max-w-6xl mx-auto px-5 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="home-logo">
          <BrandMark company={null} />
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-semibold text-zinc-300">
          <a href="/#features" className="hover:text-white transition-colors" data-testid="nav-features">{tt("Features", "Functies")}</a>
          <a href="/#story" className="hover:text-white transition-colors" data-testid="nav-story">{tt("Our story", "Ons verhaal")}</a>
          <a href="/#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">{tt("Pricing", "Prijzen")}</a>
          <a href="/#contact" className="hover:text-white transition-colors" data-testid="nav-contact">{tt("Contact", "Contact")}</a>
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <Link to="/login" data-testid="home-login-btn"
            className="bg-[#F26722] text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:bg-[#D95513] transition-colors">
            {tt("Log in", "Inloggen")}
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const { lang } = useLang();
  const tt = (en, nl) => (lang === "nl" ? nl : en);
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#F8FAFC] to-white border-b border-zinc-100">
      <div className="relative max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 text-[#F26722] font-bold text-sm bg-[#F26722]/10 px-3 py-1.5 rounded-full">
            <ShieldCheck size={15} /> {tt("Field service reporting", "Veldservicerapportage")}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A1A1A] leading-[1.05] mt-6">
            {lang === "nl"
              ? <>Van werkplaats tot<br />kantoor — in één app.</>
              : <>From workshop to<br />office — in one app.</>}
          </h1>
          <p className="text-zinc-600 mt-5 text-base max-w-lg">
            {tt(
              "Service reports, done before you leave the customer. Log the job, add parts and a signature, and send it straight to the office.",
              "Servicerapporten, klaar vóór je van de klant wegrijdt. Log de klus, voeg onderdelen en een handtekening toe, en verstuur direct naar kantoor."
            )}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/register" data-testid="hero-get-started-btn"
              className="cta-solid bg-[#F26722] text-white font-bold px-6 py-3.5 rounded-xl hover:bg-[#D95513] transition-colors flex items-center gap-2">
              {tt("Get started", "Aan de slag")} <ArrowRight size={18} />
            </Link>
            <a href="#pricing" data-testid="hero-pricing-btn"
              className="bg-white text-[#1A1A1A] font-bold px-6 py-3.5 rounded-xl hover:bg-zinc-50 transition-colors border border-zinc-200">
              {tt("See pricing", "Bekijk prijzen")}
            </a>
          </div>
        </div>
        <div className="fade-up">
          <HeroSlideshow />
        </div>
      </div>
    </section>
  );
}

function Features() {
  const { lang } = useLang();
  const [active, setActive] = useState(null);
  const L = (o) => (lang === "nl" ? o.nl : o.en);
  return (
    <section id="features" className="max-w-6xl mx-auto px-5 md:px-6 py-20 md:py-24">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A1A1A]">{lang === "nl" ? "Alles wat het veld nodig heeft" : "Everything the field needs"}</h2>
          <p className="text-zinc-500 mt-3 text-base">
            {lang === "nl"
              ? "Gebouwd voor serviceteams die elke dag aan machines werken — snel in gebruik, moeilijk kapot te krijgen."
              : "Built for service teams who work on machines every day — fast to use, hard to break."}
          </p>
        </div>
        <LangToggle />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {FEATURES.map((f) => (
          <button key={f.key} data-testid={`feature-${f.key}`} onClick={() => setActive(f)}
            className="text-left bg-[#141416] border border-zinc-800 rounded-2xl p-6 hover:-translate-y-1 transition-transform cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#F26722]/50">
            <div className="w-11 h-11 rounded-xl bg-[#1C2331] flex items-center justify-center">
              <f.icon size={22} className="text-[#F26722]" />
            </div>
            <h3 className="text-lg font-bold text-[#1A1A1A] mt-4">{L(f.title)}</h3>
            <p className="text-zinc-500 text-sm mt-2 leading-relaxed">{L(f.desc)}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#F26722] mt-4">
              {lang === "nl" ? "Lees meer" : "Learn more"} <ArrowRight size={15} />
            </span>
          </button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="bg-[#141416] border border-zinc-800 max-w-lg" data-testid="feature-dialog">
          {active && (
            <>
              <DialogHeader>
                <div className="w-12 h-12 rounded-xl bg-[#1C2331] flex items-center justify-center mb-2">
                  <active.icon size={24} className="text-[#F26722]" />
                </div>
                <DialogTitle className="text-xl font-extrabold text-[#1A1A1A]">{L(active.title)}</DialogTitle>
              </DialogHeader>
              <DialogDescription className="text-zinc-600 text-sm leading-relaxed" data-testid="feature-dialog-body">
                {L(active.long)}
              </DialogDescription>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function HowItWorks() {
  const { lang } = useLang();
  const tt = (en, nl) => (lang === "nl" ? nl : en);
  return (
    <section className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-20">
      <div className="text-center max-w-2xl mx-auto">
        <span className="text-[#F26722] font-bold text-sm uppercase tracking-wider">{tt("How it works", "Zo werkt het")}</span>
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A1A1A] mt-3">{tt("From job to office in three steps", "Van klus tot kantoor in drie stappen")}</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {STEPS.map((s, idx) => (
          <div key={idx} data-testid={`step-${idx + 1}`} className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#F26722] text-white font-black flex items-center justify-center text-sm">{idx + 1}</span>
              <s.icon size={22} className="text-[#F26722]" />
            </div>
            <h3 className="text-lg font-bold text-[#1A1A1A] mt-4">{tt(s.title.en, s.title.nl)}</h3>
            <p className="text-zinc-500 text-sm mt-2 leading-relaxed">{tt(s.body.en, s.body.nl)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Showcase() {
  const { lang } = useLang();
  const tt = (en, nl) => (lang === "nl" ? nl : en);
  return (
    <section className="bg-[#141416] border-y border-zinc-800">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24 space-y-16 md:space-y-24">
        {SHOWCASE.map((row, idx) => (
          <div key={idx} data-testid={`showcase-${idx}`} className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className={`rounded-2xl border border-zinc-200 shadow-xl overflow-hidden flex items-center justify-center p-4 h-[380px] md:h-[460px] ${idx % 2 === 1 ? "md:order-2" : ""}`}
              style={{ background: "radial-gradient(circle at 50% 25%, #F1F5F9 0%, #E2E8F0 80%)" }}>
              <img src={row.img} alt={tt(row.title.en, row.title.nl)} className="max-h-[94%] w-auto object-contain rounded-xl shadow-2xl ring-1 ring-black/5" />
            </div>
            <div className={idx % 2 === 1 ? "md:order-1" : ""}>
              <span className="text-[#F26722] font-bold text-sm uppercase tracking-wider">{tt(row.theme.en, row.theme.nl)}</span>
              <h3 className="text-2xl md:text-3xl font-extrabold text-[#1A1A1A] mt-3">{tt(row.title.en, row.title.nl)}</h3>
              <p className="text-zinc-500 mt-4 text-base leading-relaxed">{tt(row.body.en, row.body.nl)}</p>
              <ul className="mt-5 space-y-2.5">
                {(lang === "nl" ? row.bullets.nl : row.bullets.en).map((b) => (
                  <li key={b} className="flex items-center gap-2.5 text-sm font-medium text-[#1A1A1A]"><Check size={17} className="text-[#F26722] shrink-0" /> {b}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Story() {
  const { lang } = useLang();
  const tt = (en, nl) => (lang === "nl" ? nl : en);
  return (
    <section id="story" className="bg-[#141416] border-y border-zinc-800">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-20 md:py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <img src={STORY} alt="Engineer using a tablet on the factory floor"
            className="rounded-2xl w-full h-[380px] object-cover border border-zinc-800" />
        </div>
        <div>
          <span className="text-[#F26722] font-bold text-sm uppercase tracking-wider">{tt("Our story", "Ons verhaal")}</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A1A1A] mt-3">{tt("Born on the workshop floor", "Ontstaan op de werkvloer")}</h2>
          <div className="text-zinc-500 mt-5 space-y-4 text-base leading-relaxed">
            <p>{tt(
              "RV Service Tool started with a simple frustration: technicians doing great work in the field, then losing hours to paperwork back at the office. Reports got lost, parts were ordered twice, and no one had a clear picture of what happened on site.",
              "RV Service Tool begon met een simpele frustratie: monteurs die geweldig werk leveren in het veld, maar daarna uren kwijt zijn aan papierwerk op kantoor. Rapporten raakten zoek, onderdelen werden dubbel besteld en niemand had een helder beeld van wat er op locatie gebeurde."
            )}</p>
            <p>{tt(
              "We set out to fix that. Today RV Service Tool is trusted by service teams to capture every job the moment it's done — machines, parts, signatures and all — and get it to the right people instantly, whether they're on a phone in the field or at a desk in the office.",
              "Dat wilden we oplossen. Vandaag vertrouwen serviceteams op RV Service Tool om elke klus vast te leggen op het moment dat die klaar is — machines, onderdelen, handtekeningen en al — en meteen bij de juiste mensen te krijgen, of ze nu met een telefoon in het veld staan of achter een bureau op kantoor."
            )}</p>
            <p>{tt(
              "One live system, shared by the whole team. No more lost paperwork.",
              "Eén live systeem, gedeeld door het hele team. Geen zoekgeraakt papierwerk meer."
            )}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const { lang } = useLang();
  const tt = (en, nl) => (lang === "nl" ? nl : en);
  const trial = tt(PLAN.trial.en, PLAN.trial.nl);
  return (
    <section id="pricing" className="max-w-6xl mx-auto px-5 md:px-6 py-20 md:py-24">
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A1A1A]">{tt("Simple, honest pricing", "Eenvoudige, eerlijke prijzen")}</h2>
        <p className="text-zinc-500 mt-3 text-base">
          {tt(`Start with a ${trial}. Then pick one plan for your company — you only pay per user, no hidden costs.`,
              `Begin met een ${trial}. Kies daarna één plan voor je bedrijf — je betaalt alleen per gebruiker, zonder verborgen kosten.`)}
        </p>
      </div>
      <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {PLANS.map((p) => <PlanCard key={p.id} p={p} tt={tt} lang={lang} trial={trial} />)}
      </div>
      <p className="text-center text-xs text-zinc-500 mt-6" data-testid="plan-setup-note">{tt(
        `Both plans: one-time setup fee of ${PLAN.setupFee} on your first invoice. Pro replaces Standard — it is not charged on top.`,
        `Beide plannen: eenmalige instelkosten van ${PLAN.setupFee} op je eerste factuur. Pro vervangt Standard — het komt er niet bovenop.`
      )}</p>
    </section>
  );
}

function PlanCard({ p, tt, lang, trial }) {
  const dark = p.featured;
  return (
    <div data-testid={`plan-${p.id}`} className={`rounded-2xl p-8 border flex flex-col ${dark ? "surface-dark border-[#F26722] shadow-2xl" : "bg-white border-zinc-200"}`}>
      <span className={`self-start text-xs font-bold px-3 py-1 rounded-full mb-4 ${dark ? "bg-[#F26722] text-white" : "bg-[#F26722]/10 text-[#F26722]"}`}>{dark ? tt("Most complete", "Meest compleet") : trial}</span>
      <h3 className={`text-lg font-bold ${dark ? "text-white" : "text-[#1A1A1A]"}`}>{p.name}</h3>
      <p className={`text-sm mt-1 ${dark ? "text-zinc-300" : "text-zinc-500"}`}>{tt(p.tagline.en, p.tagline.nl)}</p>
      <div className="mt-5 flex items-end gap-1.5">
        <span className={`text-5xl font-extrabold ${dark ? "text-white" : "text-[#1A1A1A]"}`} data-testid={`plan-${p.id}-price`}>{p.price}</span>
        <span className="text-sm mb-1.5 text-zinc-400">{tt(PLAN.period.en, PLAN.period.nl)}</span>
      </div>
      <div className={`mt-4 flex items-center gap-2 rounded-xl px-4 py-3 border ${dark ? "bg-white/5 border-zinc-700" : "bg-zinc-50 border-zinc-200"}`}>
        <span className={`text-sm ${dark ? "text-zinc-300" : "text-zinc-500"}`}>{tt("One-time setup fee", "Eenmalige instelkosten")}</span>
        <span className="ml-auto text-lg font-extrabold text-[#F26722]">{PLAN.setupFee}</span>
      </div>
      <ul className="mt-6 space-y-3 flex-1">
        {(lang === "nl" ? p.features.nl : p.features.en).map((f) => (
          <li key={f} className={`flex items-start gap-2.5 text-sm ${dark ? "text-zinc-200" : "text-zinc-600"}`}><Check size={17} className="text-[#F26722] shrink-0 mt-0.5" /> {f}</li>
        ))}
      </ul>
      <Link to="/register" data-testid={`plan-cta-${p.id}`}
        className={`w-full mt-7 font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 ${dark ? "cta-solid bg-[#F26722] text-white hover:bg-[#D95513]" : "border-2 border-[#F26722] text-[#F26722] hover:bg-orange-50"}`}>
        {tt("Start free trial", "Start gratis proef")}
      </Link>
      <p className="text-center text-xs text-zinc-400 mt-3">{tt("No card needed to start · cancel anytime", "Geen kaart nodig · altijd opzegbaar")}</p>
    </div>
  );
}

function Contact() {
  const { lang } = useLang();
  const tt = (en, nl) => (lang === "nl" ? nl : en);
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      setSent(true);
      setForm({ name: "", email: "", message: "", website: "" });
      toast.success(tt("Message sent — we'll be in touch!", "Bericht verzonden — we nemen contact op!"));
    } catch (err) {
      const st = err.response?.status;
      if (st === 429) toast.error(tt("Too many messages. Please try again later.", "Te veel berichten. Probeer het later opnieuw."));
      else toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="surface-dark">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-20 md:py-24 grid md:grid-cols-2 gap-12 items-start">
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white">{tt("Let's talk", "Neem contact op")}</h2>
          <p className="text-zinc-300 mt-3 text-base max-w-md">
            {tt(
              "Questions about plans, onboarding or a custom setup for multiple companies? Reach out — we're happy to help.",
              "Vragen over abonnementen, onboarding of een maatwerkopzet voor meerdere bedrijven? Neem contact op — we helpen je graag."
            )}
          </p>
          <div className="mt-8 space-y-4">
            <a href="mailto:info@rvservicetool.nl" className="flex items-center gap-3 text-zinc-200 hover:text-white transition-colors" data-testid="contact-email">
              <span className="w-10 h-10 rounded-xl bg-[#F26722]/15 flex items-center justify-center"><Mail size={18} className="text-[#F26722]" /></span>
              info@rvservicetool.nl
            </a>
            <a href="tel:+31650825998" className="flex items-center gap-3 text-zinc-200 hover:text-white transition-colors" data-testid="contact-phone">
              <span className="w-10 h-10 rounded-xl bg-[#F26722]/15 flex items-center justify-center"><Phone size={18} className="text-[#F26722]" /></span>
              +31 6 50 82 59 98
            </a>
            <div className="flex items-center gap-3 text-zinc-200">
              <span className="w-10 h-10 rounded-xl bg-[#F26722]/15 flex items-center justify-center"><MapPin size={18} className="text-[#F26722]" /></span>
              {tt("Netherlands", "Nederland")}
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          {sent ? (
            <div className="text-center py-8" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-[#F26722]/20 flex items-center justify-center mx-auto">
                <Check size={28} className="text-[#F26722]" />
              </div>
              <h3 className="text-xl font-bold text-white mt-4">{tt("Message sent", "Bericht verzonden")}</h3>
              <p className="text-zinc-300 mt-2 text-sm">{tt(
                "Thanks — we've emailed you a confirmation and will be in touch shortly.",
                "Bedankt — we hebben je een bevestiging gemaild en nemen snel contact op."
              )}</p>
              <button onClick={() => setSent(false)} data-testid="contact-send-another"
                className="text-[#F26722] font-semibold text-sm mt-5">{tt("Send another message", "Nog een bericht sturen")}</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form">
              <h3 className="text-xl font-bold text-white">{tt("Send us a message", "Stuur ons een bericht")}</h3>
              <p className="text-zinc-300 mt-1 text-sm">{tt("We'll reply to the email you provide.", "We reageren op het e-mailadres dat je opgeeft.")}</p>
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")}
                name="website" data-testid="contact-website-hp"
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
              <label className="block text-xs font-bold tracking-wider text-zinc-300 uppercase mt-6 mb-2">{tt("Name", "Naam")}</label>
              <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder={tt("Your name", "Je naam")}
                className="w-full bg-[#141416] border border-zinc-800 text-white rounded-xl px-4 py-3 placeholder-zinc-500 focus:border-[#F26722] focus:outline-none" />
              <label className="block text-xs font-bold tracking-wider text-zinc-300 uppercase mt-4 mb-2">{tt("Email", "E-mail")}</label>
              <input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com"
                className="w-full bg-[#141416] border border-zinc-800 text-white rounded-xl px-4 py-3 placeholder-zinc-500 focus:border-[#F26722] focus:outline-none" />
              <label className="block text-xs font-bold tracking-wider text-zinc-300 uppercase mt-4 mb-2">{tt("Message", "Bericht")}</label>
              <textarea data-testid="contact-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder={tt("How can we help?", "Hoe kunnen we helpen?")}
                className="w-full bg-[#141416] border border-zinc-800 text-white rounded-xl px-4 py-3 placeholder-zinc-500 focus:border-[#F26722] focus:outline-none resize-none" />
              <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                className="w-full mt-5 bg-[#F26722] text-white font-bold py-3.5 rounded-xl hover:bg-[#D95513] disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <><Send size={17} /> {tt("Send message", "Verstuur bericht")}</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const { lang } = useLang();
  const tt = (en, nl) => (lang === "nl" ? nl : en);
  return (
    <footer className="surface-dark border-t border-zinc-800/60">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-zinc-400">
        <div className="flex items-center gap-2"><BrandMark company={null} /></div>
        <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6">
          <a href="mailto:info@rvservicetool.nl" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-email"><Mail size={15} className="text-[#F26722]" /> info@rvservicetool.nl</a>
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">{tt("Privacy policy", "Privacybeleid")}</Link>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">{tt("Terms and conditions", "Algemene voorwaarden")}</Link>
          <p>© {new Date().getFullYear()} RV Service Tool — {tt("Field service reporting", "Veldservicerapportage")}</p>
        </div>
      </div>
    </footer>
  );
}

export default function Home() {
  useEffect(() => {
    try {
      let vid = localStorage.getItem("rv_visitor_id");
      if (!vid) {
        vid = (window.crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(16).slice(2));
        localStorage.setItem("rv_visitor_id", vid);
      }
      if (!sessionStorage.getItem("rv_visit_logged")) {
        api.post("/analytics/visit", { visitor_id: vid, path: window.location.pathname }).catch(() => {});
        sessionStorage.setItem("rv_visit_logged", "1");
      }
    } catch (e) { /* ignore */ }
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="home-page">
      <PublicNav />
      <Hero />
      <HowItWorks />
      <Showcase />
      <Features />
      <Story />
      <Pricing />
      <Contact />
      <Footer />
    </div>
  );
}
